import React, { useState, useEffect } from 'react';
import { FaseGrupoPartidasLite, PartidaLite } from '../../../../services/faseGrupoPartidasService';
import { listarEquipesDoGrupo } from '../../../../services/faseGrupoPartidaEquipeService';
import { EquipeLite } from '../../../../services/faseGrupoPartidasService';
import { deletarPartida } from '../../../../services/partidaService';
import {
  buscarClassificacaoPorGrupo,
  LinhaClassificacaoFront,
} from '../../../../services/classificacaoService';
import type { FaseGrupoPartidaEquipe } from '../../../../types/graphql';
import TeamsTable from './TeamsTable';
import MatchesTable from './MatchesTable';
import EditGroupModal from './EditGroupModal';
import AddTeamToGroupModal from './AddTeamToGroupModal';
import CreateEditMatchModal from './CreateEditMatchModal';
import { isRodizio } from '../../../../utils/isRodizio';

interface GroupSectionProps {
  grupo: FaseGrupoPartidasLite;
  faseCategoriaTorneioId: string;
  categoriaId: string;
  onUpdate: () => void;
  canManage?: boolean;
  canEditMatches?: boolean;
  /** Modelo de disputa da categoria — usado para ocultar "Adicionar Equipe" no rodízio (grupo fixo). */
  modeloDisputa?: string | null;
}

const GroupSection: React.FC<GroupSectionProps> = ({
  grupo,
  faseCategoriaTorneioId,
  categoriaId,
  onUpdate,
  canManage = true,
  canEditMatches = true,
  modeloDisputa,
}) => {
  const [equipes, setEquipes] = useState<EquipeLite[]>([]);
  // Inicia true: evita o flash de "Nenhuma equipe neste grupo" no 1º frame, antes do effect.
  const [loadingEquipes, setLoadingEquipes] = useState(true);
  const [linhasClassificacao, setLinhasClassificacao] = useState<LinhaClassificacaoFront[]>([]);
  const [loadingClassificacao, setLoadingClassificacao] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [editingPartida, setEditingPartida] = useState<PartidaLite | null>(null);

  useEffect(() => {
    loadEquipes();
    loadClassificacao();
  }, [grupo.id]);

  const loadEquipes = async () => {
    setLoadingEquipes(true);
    setLoadError(null);
    try {
      const vinculos: FaseGrupoPartidaEquipe[] = await listarEquipesDoGrupo(grupo.id);
      const equipesList = (vinculos || [])
        .map((v) => v?.equipe)
        .filter((e): e is EquipeLite => !!e);
      setEquipes(equipesList);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
      // Sem isto, falha de rede vira "Nenhuma equipe" definitivo, sem aviso ao organizador.
      setLoadError('Não foi possível carregar os dados do grupo.');
    } finally {
      setLoadingEquipes(false);
    }
  };

  const loadClassificacao = async () => {
    setLoadingClassificacao(true);
    try {
      const linhas = await buscarClassificacaoPorGrupo(grupo.id);
      setLinhasClassificacao(linhas);
    } catch (error) {
      console.error('Erro ao carregar classificação:', error);
      setLoadError('Não foi possível carregar os dados do grupo.');
    } finally {
      setLoadingClassificacao(false);
    }
  };

  // Pool de atletas do grupo (Equipe-de-1 no rodízio) para a edição manual de
  // participantes no modal de partida. Dedup por id de atleta.
  const atletasDoGrupo = React.useMemo(() => {
    const mapa = new Map<string, { id: string; usuario?: { id: string; nome?: string; imagemUrl?: string | null } | null }>();
    equipes.forEach((e) =>
      (e.atletas ?? []).forEach((a) => {
        if (a?.id && !mapa.has(a.id)) mapa.set(a.id, a);
      }),
    );
    return Array.from(mapa.values());
  }, [equipes]);

  const handleEditGroup = () => {
    setShowEditModal(true);
  };

  const handleAddTeam = () => {
    setShowAddTeamModal(true);
  };

  const handleEditMatch = (partidaId: string) => {
    const partida = grupo.partidas.find((p) => p.id === partidaId);
    if (partida) {
      setEditingPartida(partida);
      setShowMatchModal(true);
    }
  };

  const handleDeleteMatch = async (partidaId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta partida?')) {
      return;
    }
    try {
      await deletarPartida(partidaId);
      onUpdate();
    } catch (error) {
      console.error('Erro ao deletar partida:', error);
      alert('Erro ao excluir partida');
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    onUpdate();
  };

  const handleCloseAddTeamModal = () => {
    setShowAddTeamModal(false);
    loadEquipes();
    onUpdate();
  };

  const handleCloseMatchModal = () => {
    setShowMatchModal(false);
    setEditingPartida(null);
    onUpdate();
  };

  return (
    <>
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <div className="flex-grow-1">
            <h5 className="mb-0 fw-bold">{grupo.nome || 'Grupo sem nome'}</h5>
          </div>
          {canManage && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={handleEditGroup}
                title="Editar grupo"
              >
                <i className="bi bi-pencil me-1"></i>
                Editar
              </button>
              {/* Rodízio (Rei/Rainha, Super 8) tem grupo de tamanho FIXO — adicionar
                  equipe criaria um membro sem partidas. Botão oculto (backend também rejeita). */}
              {!isRodizio(modeloDisputa) && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleAddTeam}
                  title="Adicionar equipe"
                >
                  <i className="ti-plus me-1"></i>
                  Adicionar Equipe
                </button>
              )}
            </div>
          )}
        </div>
        <div className="card-body">
          {/* Seção de Equipes */}
          <div className="mb-4">
            <h6 className="mb-3">Equipes</h6>
            {loadingEquipes || loadingClassificacao ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
              </div>
            ) : loadError ? (
              <div className="alert alert-danger py-2 mb-0" role="alert" data-testid="group-load-error">
                {loadError}
              </div>
            ) : (
              <TeamsTable
                equipes={equipes}
                linhasClassificacao={linhasClassificacao}
              />
            )}
          </div>

          {/* Seção de Partidas */}
          <div>
            <h6 className="mb-3">Partidas</h6>
            <MatchesTable
              partidas={grupo.partidas || []}
              onEdit={handleEditMatch}
              canManage={canManage}
            />
          </div>
        </div>
      </div>

      {/* Modais */}
      {showEditModal && (
        <EditGroupModal
          grupo={grupo}
          faseCategoriaTorneioId={faseCategoriaTorneioId}
          onClose={handleCloseEditModal}
          onSuccess={handleCloseEditModal}
        />
      )}

      {showAddTeamModal && (
        <AddTeamToGroupModal
          grupoId={grupo.id}
          faseCategoriaTorneioId={faseCategoriaTorneioId}
          categoriaId={categoriaId}
          onClose={handleCloseAddTeamModal}
          onSuccess={handleCloseAddTeamModal}
        />
      )}

      {showMatchModal && (
        <CreateEditMatchModal
          partida={editingPartida}
          categoriaId={categoriaId}
          faseCategoriaTorneioId={faseCategoriaTorneioId}
          grupoId={grupo.id}
          onClose={handleCloseMatchModal}
          onSuccess={handleCloseMatchModal}
          canEdit={canEditMatches}
          atletasDoGrupo={atletasDoGrupo}
        />
      )}
    </>
  );
};

export default GroupSection;

